/**
 * SMS segment calculator — a faithful port of Twilio's message-segment-calculator
 * (github.com/TwilioDevEd/message-segment-calculator).
 *
 * A message is GSM-7 unless it contains a character outside the GSM 03.38
 * alphabet, in which case the whole message is UCS-2. An SMS holds 140 octets
 * = 1120 bits; once a message needs more than one segment, every segment
 * reserves a 6-octet (48-bit) user-data header to chain them, leaving 1072 bits
 * each (→ 153 GSM-7 chars or 67 UCS-2 chars per segment).
 */

type SmsEncoding = 'GSM-7' | 'UCS-2'

export interface SmsSegmentInfo {
  /** Number of characters (GSM extended chars count as 2; UCS-2 by grapheme). */
  charCount: number
  /** Number of SMS segments the message would be sent as. */
  segments: number
  encoding: SmsEncoding
}

/** GSM 03.38 basic alphabet — each character costs one 7-bit code unit. */
const GSM_BASIC = new Set(
  `@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà`,
)

/** GSM 03.38 extension table — each costs two 7-bit code units (ESC + char). */
const GSM_EXTENDED = new Set(`\f^{}\\[~]|€`)

const MAX_BITS_PER_SEGMENT = 1120 // 140 octets
const USER_DATA_HEADER_BITS = 48 // 6 octets, reserved on concatenated segments

/** A character the GSM-7 alphabet can carry — anything else forces the whole
 *  message to UCS-2 (and its 70-char segments). */
export function isGsmChar(grapheme: string): boolean {
  return GSM_BASIC.has(grapheme) || GSM_EXTENDED.has(grapheme)
}

/** A GSM character that costs two code units (ESC + char): `{}[]~^\|€`. */
export function isGsmExtendedChar(grapheme: string): boolean {
  return GSM_EXTENDED.has(grapheme)
}

/** Split into grapheme clusters so emoji families count as one character, like
 *  Twilio does. Falls back to code points where Intl.Segmenter is unavailable. */
export function splitGraphemes(text: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' })
    return Array.from(segmenter.segment(text), (part) => part.segment)
  }
  return Array.from(text)
}

/** Bits a single grapheme occupies in the chosen encoding. */
function bitsForGrapheme(grapheme: string, encoding: SmsEncoding): number {
  if (encoding === 'UCS-2') return 16 * grapheme.length // 16 bits per UTF-16 unit
  return GSM_EXTENDED.has(grapheme) ? 14 : 7
}

/** Greedy-pack the per-grapheme bit costs into segments, never splitting a
 *  character across a boundary. A message that fits in one segment uses no
 *  header (the full 1120 bits); anything larger pays the header on every part. */
function countSegments(bitSizes: number[]): number {
  const total = bitSizes.reduce((sum, bits) => sum + bits, 0)
  if (total === 0) return 0
  if (total <= MAX_BITS_PER_SEGMENT) return 1

  const capacity = MAX_BITS_PER_SEGMENT - USER_DATA_HEADER_BITS
  let segments = 1
  let used = 0
  for (const bits of bitSizes) {
    if (used + bits > capacity) {
      segments += 1
      used = 0
    }
    used += bits
  }
  return segments
}

/** Compute the encoding, character count, and segment count for an SMS body. */
export function computeSmsSegments(text: string): SmsSegmentInfo {
  const graphemes = splitGraphemes(text)
  const encoding: SmsEncoding = graphemes.some(
    (grapheme) => grapheme.length >= 2 || !isGsmChar(grapheme),
  )
    ? 'UCS-2'
    : 'GSM-7'

  const charCount =
    encoding === 'UCS-2'
      ? graphemes.length
      : graphemes.reduce(
          (count, grapheme) => count + (GSM_EXTENDED.has(grapheme) ? 2 : 1),
          0,
        )

  const segments = countSegments(
    graphemes.map((grapheme) => bitsForGrapheme(grapheme, encoding)),
  )

  return { charCount, segments, encoding }
}
